import { useState, useEffect, useRef, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, CheckCircle, AlertTriangle, Clock, UserPlus, UserCog, MessageSquare, Boxes, AtSign, X, Bug, Lightbulb, FolderKanban, ArrowRightLeft, Rocket } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useTasks } from '../../hooks/useTasks'
import { useAuth } from '../../hooks/useAuth'
import { formatDate } from '../../lib/helpers'
import { useMentionNotifications } from '../../hooks/useMentionNotifications'
import { useConversations } from '../../hooks/useConversations'
import { useTodoAssignmentNotifications } from '../../hooks/useTodoAssignmentNotifications'
import { useProjectActivityNotifications } from '../../hooks/useProjectActivityNotifications'
import { formatProjectActivity } from '../../lib/projectActivity'

// Dev Projects activity (migration 119) — icon/color per event kind.
const PROJECT_ACTIVITY_STYLES = {
  project_bug_reported:    { icon: <Bug size={14} />,            color: 'text-red-600 bg-red-500/15' },
  project_request_created: { icon: <Lightbulb size={14} />,      color: 'text-amber-600 bg-amber-500/15' },
  project_feature_created: { icon: <FolderKanban size={14} />,   color: 'text-violet-600 bg-violet-500/15' },
  project_feature_moved:   { icon: <ArrowRightLeft size={14} />, color: 'text-sky-600 bg-sky-500/15' },
  project_comment:         { icon: <MessageSquare size={14} />,  color: 'text-brand-600 bg-brand-500/15' },
  project_promotion:       { icon: <Rocket size={14} />,         color: 'text-emerald-600 bg-emerald-500/15' },
}

function getNotifications(myTasks, profile, unsetupUsers, recentComments, hubInvites, hubMentions, dmConversations, todoAssignments = [], projectActivities = []) {
  const notifications = []
  const now = new Date()

  // Dev Projects activity — outbox rows preformatted by projectActivity.js.
  projectActivities.forEach(row => {
    const n = formatProjectActivity(row)
    if (!n) return
    const style = PROJECT_ACTIVITY_STYLES[n.kind] || { icon: <FolderKanban size={14} />, color: 'text-violet-600 bg-violet-500/15' }
    notifications.push({
      id: `proj-${row.id}`,
      type: 'project-activity',
      icon: style.icon,
      color: style.color,
      title: n.title,
      body: n.body,
      link: n.link,
      outboxId: row.id,
      time: n.time,
      priority: 0.45,
    })
  })

  // Hub invitations
  hubInvites.forEach(inv => {
    notifications.push({
      id: `hub-invite-${inv.hub_id}`,
      type: 'hub-invite',
      icon: <Boxes size={14} />,
      color: 'text-purple-600 bg-purple-500/15',
      title: 'Hub invitation',
      body: `You were added to "${inv.hub_name}"`,
      link: `/hub/${inv.hub_id}`,
      time: inv.created_at,
      priority: 0.3,
    })
  })

  // @mentions — combined hub + task chat. Each entry is tagged with `source`.
  hubMentions.forEach(m => {
    if (m.source === 'task_chat') {
      notifications.push({
        id: `task-mention-${m.id}`,
        type: 'task-mention',
        icon: <AtSign size={14} />,
        color: 'text-brand-600 bg-brand-500/15',
        title: `${m.author?.full_name || 'Someone'} mentioned you`,
        body: `in ${m.task_title || 'a task'} chat`,
        taskChatTaskId: m.task_id,
        time: m.created_at,
        priority: 0.2,
      })
      return
    }
    const moduleLabel = { chat: 'Campfire', message: 'Message Board', message_reply: 'Message Board', todo_note: 'To-dos', todo_comment: 'To-dos', todo_list: 'To-dos' }[m.entity_type] || 'Hub'
    notifications.push({
      id: `hub-mention-${m.id}`,
      type: 'hub-mention',
      icon: <AtSign size={14} />,
      color: 'text-brand-600 bg-brand-500/15',
      title: `${m.mentioner?.full_name || 'Someone'} mentioned you`,
      body: `in ${m.hub?.name || 'a hub'} — ${moduleLabel}`,
      link: `/hub/${m.hub_id}`,
      mentionId: m.id,
      time: m.created_at,
      priority: 0.2,
    })
  })

  // To-do assignments (last 7d) — see useTodoAssignmentNotifications.
  // Routed straight to the deep-link item URL the rest of the app uses
  // (matches AssignTodoFromChatModal's post-create navigate).
  todoAssignments.forEach((a) => {
    const hubName = a.item?.hub?.name || 'a hub'
    const listTitle = a.item?.list?.title
    const itemTitle = a.item?.title || 'a to-do'
    const link = a.item?.hub_id && a.item?.list_id && a.item_id
      ? `/hub/${a.item.hub_id}/todos/${a.item.list_id}/items/${a.item_id}`
      : (a.item?.hub_id ? `/hub/${a.item.hub_id}` : null)
    notifications.push({
      id: `todo-assign-${a.id}`,
      type: 'todo-assigned',
      icon: <CheckCircle size={14} />,
      color: 'text-emerald-600 bg-emerald-500/15',
      title: `${a.assigner?.full_name || 'Someone'} assigned you a to-do`,
      body: `${itemTitle}${listTitle ? ` in ${listTitle}` : ''} — ${hubName}`,
      link,
      time: a.created_at,
      priority: 0.4,
    })
  })

  // DM unread — one aggregated entry per conversation
  dmConversations.forEach(c => {
    if (!c.unread || c.unread <= 0 || !c.other_profile) return
    notifications.push({
      id: `dm-${c.id}`,
      type: 'dm',
      icon: <MessageSquare size={14} />,
      color: 'text-brand-600 bg-brand-500/15',
      title: `${c.unread} new message${c.unread > 1 ? 's' : ''} from ${c.other_profile.full_name}`,
      body: c.last_message_preview || '',
      convId: c.id,
      time: c.last_message_at,
      priority: 0.25,
    })
  })

  // Recent comments on my tasks by other people
  recentComments.forEach(c => {
    notifications.push({
      id: `comment-${c.id}`,
      type: 'comment',
      icon: <MessageSquare size={14} />,
      color: 'text-brand-600 bg-brand-500/15',
      title: `${c.author?.full_name || 'Someone'} commented`,
      body: `${c.task_title}: ${c.content?.slice(0, 60)}${c.content?.length > 60 ? '...' : ''}`,
      taskId: c.task_id,
      time: c.created_at,
      priority: 0.5,
    })
  })

  // Admin/Manager: users needing setup (no teams assigned)
  if ((profile?.role === 'Admin' || profile?.role === 'Manager') && unsetupUsers.length > 0) {
    notifications.push({
      id: 'users-need-setup',
      type: 'admin',
      icon: <UserCog size={14} />,
      color: 'text-amber-600 bg-amber-500/15',
      title: `${unsetupUsers.length} user${unsetupUsers.length > 1 ? 's' : ''} need${unsetupUsers.length === 1 ? 's' : ''} setup`,
      body: unsetupUsers.map(u => u.full_name || u.email).join(', '),
      link: '/settings',
      time: unsetupUsers[0]?.created_at,
      priority: 0,
    })
  }

  // Pending acceptance tasks
  const pending = myTasks.filter(t => t.acceptance_status === 'Pending')
  pending.forEach(t => {
    notifications.push({
      id: `pending-${t.id}`,
      type: 'pending',
      icon: <UserPlus size={14} />,
      color: 'text-yellow-600 bg-yellow-500/15',
      title: 'Task awaiting acceptance',
      body: t.title,
      taskId: t.id,
      time: t.date_assigned,
      priority: 1,
    })
  })

  // Overdue / RED tasks
  const overdue = myTasks.filter(t => t.priority === 'red' && t.status !== 'Done' && t.acceptance_status !== 'Declined')
  overdue.forEach(t => {
    notifications.push({
      id: `overdue-${t.id}`,
      type: 'overdue',
      icon: <AlertTriangle size={14} />,
      color: 'text-red-600 bg-red-500/15',
      title: 'Overdue task',
      body: t.title,
      taskId: t.id,
      time: t.due_date || t.last_updated,
      priority: 2,
    })
  })

  // Due soon (orange) tasks — within 12 hours
  const urgent = myTasks.filter(t => t.priority === 'orange' && t.status !== 'Done' && t.acceptance_status !== 'Declined')
  urgent.forEach(t => {
    notifications.push({
      id: `urgent-${t.id}`,
      type: 'urgent',
      icon: <Clock size={14} />,
      color: 'text-orange-600 bg-orange-500/15',
      title: 'Due soon',
      body: t.title,
      taskId: t.id,
      time: t.due_date || t.last_updated,
      priority: 3,
    })
  })

  // Recently assigned (last 24h)
  const dayAgo = new Date(now - 24 * 60 * 60 * 1000)
  const recent = myTasks.filter(t =>
    new Date(t.date_assigned) > dayAgo &&
    t.acceptance_status === 'Accepted' &&
    t.status === 'Not Started'
  )
  recent.forEach(t => {
    notifications.push({
      id: `new-${t.id}`,
      type: 'new',
      icon: <CheckCircle size={14} />,
      color: 'text-sky-600 bg-sky-500/15',
      title: 'New task assigned',
      body: t.title,
      taskId: t.id,
      time: t.date_assigned,
      priority: 4,
    })
  })

  // External users (Agent / Client) only see hub invites, hub @mentions, and DMs.
  // Task-related entries (comment, pending, overdue, urgent, new) and the
  // admin "users-need-setup" card are filtered out so externals don't see
  // internal-only noise. Keep this list in sync with any new hub/to-do
  // notification types added above.
  const isExternal = profile?.role === 'Agent' || profile?.role === 'Client'
  const externalAllowedTypes = new Set(['hub-invite', 'hub-mention', 'task-mention', 'dm', 'todo-assigned'])
  const filtered = isExternal
    ? notifications.filter(n => externalAllowedTypes.has(n.type))
    : notifications

  // Sort by priority then by time
  return filtered.sort((a, b) => a.priority - b.priority || new Date(b.time) - new Date(a.time))
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export default function NotificationBell({ onTaskClick }) {
  // Perf TODO: for external users (Agent/Client) this still runs the full task
  // fetch even though task-related notifications get filtered out in
  // getNotifications(). useTasks() doesn't currently support a `skip` option;
  // if/when it does, pass { skip: profile?.role === 'Agent' || profile?.role === 'Client' }
  // here to avoid the wasted query.
  const { myTasks, tasks } = useTasks()
  const { profile, isAdmin, isManager } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [unsetupUsers, setUnsetupUsers] = useState([])
  const [recentComments, setRecentComments] = useState([])
  const [hubInvites, setHubInvites] = useState([])
  const { mentions: hubMentions, markSeen: markMentionSeen } = useMentionNotifications()
  const { conversations: dmConversations } = useConversations()
  const todoAssignments = useTodoAssignmentNotifications()
  const { activities: projectActivities, markSeen: markProjectSeen, markAllSeen: markAllProjectSeen } = useProjectActivityNotifications()
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pe-dismissed-notifs') || '[]')
    } catch { return [] }
  })
  const panelRef = useRef(null)
  // Stable per-mount id so HMR / StrictMode double-mounts don't collide on
  // the same realtime channel name (which would tear down the live
  // subscription when one of the two unmounts).
  const instanceId = useId()

  // Fetch recent comments on tasks I'm involved with (assignee or assigner), by other people
  // Use a ref to track task IDs so the effect only depends on profile, not the tasks array
  const taskIdsRef = useRef([])
  const tasksRef = useRef([])
  taskIdsRef.current = tasks
    .filter(t => t.assigned_to === profile?.id || t.assigned_by === profile?.id || t.task_assignees?.some(ta => ta.profile_id === profile?.id))
    .map(t => t.id)
  tasksRef.current = tasks

  useEffect(() => {
    if (!profile?.id) return

    async function fetchComments() {
      const ids = taskIdsRef.current
      if (ids.length === 0) return

      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      const { data } = await supabase
        .from('comments')
        .select('id, task_id, content, created_at, author_id, author:profiles(full_name, avatar_url)')
        .in('task_id', ids)
        .neq('author_id', profile.id)
        .gte('created_at', dayAgo)
        .order('created_at', { ascending: false })
        .limit(20)

      if (data) {
        const taskMap = Object.fromEntries(tasksRef.current.map(t => [t.id, t.title]))
        setRecentComments(data.map(c => ({ ...c, task_title: taskMap[c.task_id] || 'Task' })))
      }
    }

    // Initial fetch after a short delay to let tasks load
    const timer = setTimeout(fetchComments, 1000)

    // Realtime: refetch when new comments arrive
    const channel = supabase
      .channel(`comments-notif:${profile.id}:${instanceId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'comments' }, () => fetchComments())
      .subscribe()
    return () => { clearTimeout(timer); supabase.removeChannel(channel) }
  }, [profile?.id])

  // Admin/Manager: fetch users with no team assignments
  useEffect(() => {
    if (!isManager) return
    async function fetchUnsetup() {
      // Get all profiles, then filter to those with no profile_teams rows
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, created_at, profile_teams!profile_teams_profile_id_fkey(team_id)')
        .order('created_at', { ascending: false })
      if (profiles) {
        const unsetup = profiles.filter(p => !p.profile_teams || p.profile_teams.length === 0)
        setUnsetupUsers(unsetup)
      }
    }
    fetchUnsetup()

    // Re-check when profiles table changes (new signup) or team membership
    // changes. Critically, IGNORE updates that only touch `last_seen_at` —
    // every online user emits a 60s heartbeat that bumps that column, which
    // for an admin/manager would otherwise trigger fetchUnsetup once per
    // active user per minute (~every 8-12s of perceived activity, matching
    // the "page refreshes every 8-10s" complaint).
    const channel = supabase
      .channel(`profiles-admin-notif:${profile?.id ?? 'anon'}:${instanceId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload) => {
        if (payload.eventType === 'UPDATE') {
          const o = payload.old || {}
          const n = payload.new || {}
          const changedKeys = Object.keys(n).filter(k => o[k] !== n[k])
          // Only refetch when something the unsetup-users predicate reads
          // (team_id, role, full_name, email) actually changed. Heartbeats
          // and avatar uploads are noise here.
          const RELEVANT = new Set(['team_id', 'role', 'full_name', 'email', 'reports_to'])
          if (changedKeys.length > 0 && !changedKeys.some(k => RELEVANT.has(k))) return
        }
        fetchUnsetup()
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profile_teams' }, () => fetchUnsetup())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [isManager])

  // Hub invitations — recent hub_members rows where I was added (not by me creating the hub)
  useEffect(() => {
    if (!profile?.id) return
    async function fetchHubInvites() {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const { data } = await supabase
        .from('hub_members')
        .select('hub_id, role, created_at, hub:hubs!hub_members_hub_id_fkey(name, created_by)')
        .eq('profile_id', profile.id)
        .gte('created_at', weekAgo)
        .order('created_at', { ascending: false })
      if (data) {
        // Only show invites where someone else added me (not hubs I created)
        const invites = data
          .filter(m => m.hub?.created_by !== profile.id)
          .map(m => ({ hub_id: m.hub_id, hub_name: m.hub?.name || 'Hub', created_at: m.created_at }))
        setHubInvites(invites)
      }
    }
    fetchHubInvites()

    const channel = supabase
      .channel(`hub-invite-notif:${profile.id}:${instanceId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'hub_members', filter: `profile_id=eq.${profile.id}` },
        () => fetchHubInvites()
      )
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [profile?.id])

  // Exclude tasks the user personally archived (migration 105): archiving is
  // meant to clear a task off your lists, so the bell shouldn't keep nagging
  // about an archived task being overdue / pending / due-soon.
  const activeMyTasks = myTasks.filter(t => !t.archived)
  const allNotifications = getNotifications(activeMyTasks, profile, unsetupUsers, recentComments, hubInvites, hubMentions, dmConversations, todoAssignments, projectActivities)
  const notifications = allNotifications.filter(n => !dismissed.includes(n.id))
  const count = notifications.length

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  // Persist dismissed
  useEffect(() => {
    localStorage.setItem('pe-dismissed-notifs', JSON.stringify(dismissed))
  }, [dismissed])

  function dismiss(id) {
    setDismissed(prev => [...prev, id])
  }

  // Project-activity rows use a durable DB seen-flag (delivered_to_bell_at)
  // instead of localStorage, so dismissals survive across devices.
  function handleDismiss(n) {
    dismiss(n.id)
    if (n.outboxId) markProjectSeen(n.outboxId)
  }

  function clearAll() {
    setDismissed(allNotifications.map(n => n.id))
    markAllProjectSeen()
    setIsOpen(false)
  }

  function handleNotifClick(n) {
    dismiss(n.id)
    if (n.outboxId) markProjectSeen(n.outboxId)
    if (n.mentionId) markMentionSeen(n.mentionId)
    if (n.taskChatTaskId) {
      // Open the task detail (which hosts the task chat). Seen-tracking
      // for task-chat mentions happens implicitly: once the viewer opens
      // the chat, mark_conversation_read updates last_read_at, which the
      // mentions query filters against.
      window.dispatchEvent(new CustomEvent('open-task', { detail: { taskId: n.taskChatTaskId } }))
    } else if (n.convId) {
      window.dispatchEvent(new CustomEvent('pe-chat-open', { detail: { conversationId: n.convId } }))
    } else if (n.link) {
      navigate(n.link)
    } else if (n.taskId) {
      onTaskClick?.(n.taskId)
    }
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border shadow-soft text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-dark-hover transition-all duration-200"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Bell size={18} />
        {count > 0 && (
          <motion.span
            className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 15 }}
          >
            {count > 9 ? '9+' : count}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-white dark:bg-dark-card rounded-2xl shadow-elevated border border-slate-200 dark:border-dark-border z-50 overflow-hidden"
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 dark:border-dark-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Notifications</h3>
              {count > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-8 text-center">
                  <Bell size={24} className="text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400 dark:text-slate-500">All caught up!</p>
                </div>
              ) : (
                notifications.map((n, i) => (
                  <motion.div
                    key={n.id}
                    className="px-4 py-3 border-b border-slate-100 dark:border-dark-border hover:bg-slate-50 dark:hover:bg-dark-hover transition-colors cursor-pointer flex gap-3"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => handleNotifClick(n)}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${n.color}`}>
                      {n.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{n.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{n.body}</p>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{timeAgo(n.time)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDismiss(n) }}
                      className="p-1 rounded-lg text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-dark-hover transition-colors flex-shrink-0"
                      aria-label="Dismiss notification"
                    >
                      <X size={12} />
                    </button>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
